import axios from 'axios';
import { ISSUE_API_ROOT } from '../constants';

export const ISSUE_ATTACHMENT_TYPES = [
  { label: 'Screenshot', value: 'SCREENSHOT' },
  { label: 'Log', value: 'LOG' },
  { label: 'Other', value: 'OTHER' },
];

export const getIssueAttachmentAccept = fileExtensions =>
  (Array.isArray(fileExtensions) ? fileExtensions : [])
    .map(extension => `${extension || ''}`.trim().toLowerCase())
    .filter(Boolean)
    .map(extension =>
      extension.includes('/') ? extension : `.${extension.replace(/^\./, '')}`,
    )
    .join(',');

export const injectErrorPath = (response, path) => ({
  ...response,
  errors: Array.isArray(response?.errors)
    ? response.errors.map(error => ({ ...error, path }))
    : response?.errors,
});

export const getIssueAttachments = ({ dataProvider, issueId }) =>
  dataProvider.get(`${ISSUE_API_ROOT}/${issueId}/attachments`);

export const initiateIssueAttachmentUpload = ({
  dataProvider,
  issueId,
  file,
  attachmentType,
}) =>
  dataProvider.post(`${ISSUE_API_ROOT}/${issueId}/attachments`, {
    fileName: file.name,
    fileSize: file.size,
    contentType: file.type || undefined,
    attachmentType,
  });

export const confirmIssueAttachmentUpload = ({
  dataProvider,
  issueId,
  attachmentId,
}) =>
  dataProvider.post(
    `${ISSUE_API_ROOT}/${issueId}/attachments/${attachmentId}/confirm`,
  );

export const getIssueAttachmentDownload = ({
  dataProvider,
  issueId,
  attachmentId,
}) =>
  dataProvider.get(
    `${ISSUE_API_ROOT}/${issueId}/attachments/${attachmentId}/download`,
  );

export const removeIssueAttachment = ({
  dataProvider,
  issueId,
  attachmentId,
}) =>
  dataProvider.remove(
    `${ISSUE_API_ROOT}/${issueId}/attachments/${attachmentId}`,
  );

export const uploadIssueAttachment = async ({
  dataProvider,
  issueId,
  file,
  attachmentType = 'OTHER',
}) => {
  const path = file?.name || '';
  try {
    const initiateResponse = await initiateIssueAttachmentUpload({
      dataProvider,
      issueId,
      file,
      attachmentType,
    });

    if (!initiateResponse?.ok) {
      return injectErrorPath(initiateResponse, path);
    }

    await axios({
      method: 'put',
      url: initiateResponse.data?.url,
      data: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
    });

    const confirmResponse = await confirmIssueAttachmentUpload({
      dataProvider,
      issueId,
      attachmentId: initiateResponse.data?.attachmentId,
    });

    return confirmResponse?.ok
      ? confirmResponse
      : injectErrorPath(confirmResponse, path);
  } catch (err) {
    return {
      ok: false,
      errors: [{ message: err.message, path }],
    };
  }
};

export const uploadIssueAttachments = async ({
  dataProvider,
  issueId,
  files,
}) =>
  Promise.all(
    (files || []).map(async item => {
      const result = await uploadIssueAttachment({
        dataProvider,
        issueId,
        file: item.file,
        attachmentType: item.attachmentType || 'OTHER',
      });
      return { ...result, fileName: item.file?.name };
    }),
  );

// Create-flow staging: files are uploaded before the issue exists, and the
// create request then references them so creation is all-or-nothing.
export const stageIssueAttachmentUpload = async ({
  dataProvider,
  file,
  attachmentType = 'OTHER',
}) => {
  const path = file?.name || '';
  try {
    const initiateResponse = await dataProvider.post(
      `${ISSUE_API_ROOT}/attachment-uploads`,
      {
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type || undefined,
        attachmentType,
      },
    );

    if (!initiateResponse?.ok) {
      return injectErrorPath(initiateResponse, path);
    }

    await axios({
      method: 'put',
      url: initiateResponse.data?.url,
      data: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
      },
    });

    return {
      ok: true,
      uploadId: initiateResponse.data?.uploadId,
      fileName: file.name,
      attachmentType,
    };
  } catch (err) {
    return {
      ok: false,
      errors: [{ message: err.message, path }],
    };
  }
};

export const stageIssueAttachmentUploads = async ({ dataProvider, files }) =>
  Promise.all(
    (files || []).map(async item => {
      const result = await stageIssueAttachmentUpload({
        dataProvider,
        file: item.file,
        attachmentType: item.attachmentType || 'OTHER',
      });
      return { ...result, fileName: item.file?.name };
    }),
  );
